import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { TIDS } from "@/constants/testIds";

export default function ClaimAccessPage() {
    const { claimAccess } = useAuth();
    const navigate = useNavigate();
    const [code, setCode] = useState("");
    const [inmateNumber, setInmateNumber] = useState("");
    const [dob, setDob] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await claimAccess({
                code: code.trim().toUpperCase(),
                inmate_number: inmateNumber.trim(),
                date_of_birth: dob,
            });
            navigate("/inmate", { replace: true });
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not verify access. Please check your details.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-phoenix-mesh flex flex-col">
            <header className="max-w-6xl mx-auto w-full px-6 py-6 flex items-center justify-between">
                <PhoenixWordmark />
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-phoenix-navy transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-7 animate-fade-up">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-phoenix-navy text-white rounded-2xl mb-4 shadow-phoenix-card">
                            <KeyRound className="w-6 h-6" />
                        </div>
                        <h1 className="font-display font-bold text-3xl text-phoenix-navy mb-2">
                            Welcome to Blue Phoenix
                        </h1>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            Enter your access code along with your inmate number and date of birth to begin.
                        </p>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        data-testid={TIDS.claimForm}
                        className="bg-white p-7 rounded-3xl shadow-phoenix-card border border-border/60 space-y-5 animate-fade-up"
                        style={{ animationDelay: "60ms" }}
                    >
                        <div>
                            <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                Access Code
                            </label>
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                required
                                maxLength={12}
                                data-testid={TIDS.claimCode}
                                className="w-full px-4 py-3.5 border border-border rounded-xl text-lg font-mono tracking-[0.18em] uppercase focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                placeholder="ABCD1234"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                Inmate Number
                            </label>
                            <input
                                type="text"
                                value={inmateNumber}
                                onChange={(e) => setInmateNumber(e.target.value)}
                                required
                                data-testid={TIDS.claimInmateNumber}
                                className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                placeholder="BP-1001"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                Date of Birth
                            </label>
                            <input
                                type="date"
                                value={dob}
                                onChange={(e) => setDob(e.target.value)}
                                required
                                data-testid={TIDS.claimDob}
                                className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                            />
                        </div>

                        {error && (
                            <div
                                data-testid={TIDS.claimError}
                                className="flex items-start gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-xl px-3.5 py-2.5"
                            >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid={TIDS.claimSubmit}
                            className="w-full py-3.5 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Continue
                        </button>

                        <div className="text-center pt-1">
                            <Link
                                to="/forgot-access-code"
                                data-testid="forgot-access-code-link"
                                className="text-xs font-medium text-phoenix-navy hover:underline"
                            >
                                Lost your access code?
                            </Link>
                        </div>

                        <div className="text-center text-xs text-muted-foreground pt-3 border-t border-border/60">
                            Your wellness data is private. Conversations and journals are only reviewed if a safety concern is flagged.
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
